'use client'

/**
 * The side navigation's client shell (#15618): sai-ui's `SidebarProvider`,
 * the sidebar, and a `SidebarInset` that holds the existing application
 * frame UNCHANGED.
 *
 * WHY NOT A THIRD GRID TRACK. `AppFrame` is a two-track grid (pane | AI
 * sidecar) whose pane element must never change identity, so React bails out
 * of the route subtree when the sidecar toggles (#12946). The sidebar wraps
 * that grid from outside instead: on desktop sai-ui's `Sidebar` is
 * `position: fixed` with an in-flow gap element, so the inset — and the frame
 * inside it — is simply narrower, and the pane's measured-width breakpoints
 * (`data-app-pane-w`, the `pane-*` variants) narrow with it.
 *
 * DOCUMENT SCROLLING IS KEPT. The classroom product makes `<main>` the scroll
 * container (`h-svh`); this shell does not, because ~19 pages pin their
 * headers with `APP_HEADER_STICKY_CLASS` against document scroll. Hence no
 * `overflow-hidden` on the inset — that would make it a second scroller.
 *
 * Settings match the classroom product: 224px wide, a 72px (4.5rem) icon
 * rail. The collapse choice is CONTROLLED here and persisted in a cookie the
 * root layout reads (`sidebar-state-cookie.ts`), so the server renders the
 * viewer's choice and a hard load does not shift the page after hydration.
 *
 * `--app-nav-width` is published on the inset — the drawn sidebar's width, or
 * 0 on mobile (where it is a Sheet) and on routes that hide it — so
 * viewport-pinned controls (the recording bar's `.app-sidecar-clear-centered`)
 * centre on the pane between the sidebar and the sidecar. The desktop value
 * arrives as `--app-nav-width-desktop`; globals.css zeroes it below `md` with
 * a media query, because sai-ui's `isMobile` is only known after hydration.
 */

import { SidebarInset, SidebarProvider } from '@sai/ui/components'
import { useSidebar } from '@sai/ui/hooks'
import { usePathname } from 'next/navigation'
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useState,
} from 'react'

import { Menu } from '@/components/icons'
import { AppChromeProvider } from '@/contexts/app-chrome-context'

import { shouldHideSidebar } from './hidden-paths'
import { serializeSidebarStateCookie } from './sidebar-state-cookie'

/** The classroom product's widths: `width="224px"`, `sidebarWidthIcon`. */
export const SIDEBAR_WIDTH = '224px'
export const SIDEBAR_WIDTH_ICON = '4.5rem'
/**
 * The inset's id. The skip link that used to aim here moved to the root
 * layout in #14982 (`SkipToMainContent`), which aims at `main#main-content`.
 */
export const APP_CONTENT_ID = 'app-content'

function SideNavigationInset({
  sidebarRendered,
  children,
}: {
  sidebarRendered: boolean
  children: ReactNode
}) {
  const { open } = useSidebar()
  const navWidth = !sidebarRendered
    ? '0px'
    : open
      ? 'var(--sidebar-width)'
      : 'var(--sidebar-width-icon)'

  return (
    <SidebarInset
      id={APP_CONTENT_ID}
      data-app-side-nav-inset=""
      // `min-w-0`: a flex item's automatic minimum is its content width, so
      // without it one wide table would push the inset past the viewport.
      className="min-w-0 outline-none"
      style={{ '--app-nav-width-desktop': navWidth } as CSSProperties}
    >
      {children}
    </SidebarInset>
  )
}

/**
 * Below `md` the sidebar is a Sheet; this is its way in. 44px target.
 *
 * Pinned BELOW the banner stack (feedback banner, then the impersonation /
 * demo / presentation banners measured into `--header-height`) rather than
 * over it: that stack is sticky at `z-50`, and at `top-2` the trigger sat
 * under the demo bar — the only way into navigation, hidden.
 */
function MobileSidebarTrigger() {
  const { toggleSidebar, openMobile } = useSidebar()
  return (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label="Open navigation"
      aria-expanded={openMobile}
      data-testid="app-sidebar-mobile-trigger"
      className="fixed left-2 top-[calc(var(--feedback-banner-height,0px)+var(--header-height,0px)+0.5rem)] z-nav flex size-8 pointer-coarse:size-11 items-center justify-center rounded-md bg-surface-elevated text-text-primary shadow-sm md:hidden"
    >
      <Menu className="size-5" />
    </button>
  )
}

export function SideNavigationShell({
  sidebar,
  defaultOpen,
  children,
}: {
  /** `AppSidebarHost` in its Suspense boundary. */
  sidebar: ReactNode
  /** The viewer's persisted choice, read from the cookie by the layout. */
  defaultOpen: boolean
  children: ReactNode
}) {
  const pathname = usePathname() ?? ''
  const sidebarRendered = !shouldHideSidebar(pathname)
  const [open, setOpen] = useState(defaultOpen)
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    // biome-ignore lint/suspicious/noDocumentCookie: one first-party preference cookie; the Cookie Store API is not available in every supported browser.
    document.cookie = serializeSidebarStateCookie(next)
  }, [])

  return (
    <SidebarProvider
      width={SIDEBAR_WIDTH}
      sidebarWidthIcon={SIDEBAR_WIDTH_ICON}
      open={open}
      onOpenChange={handleOpenChange}
      data-app-side-nav=""
    >
      <AppChromeProvider sidebarRendered={sidebarRendered}>
        {sidebarRendered && (
          // `contents`: no box of its own, so the sidebar's gap element stays
          // a flex item of the provider. It exists so an error page's header
          // can hide the whole slot from CSS (`data-side-nav-suppressed`).
          <div data-app-sidebar-slot="" className="contents">
            {sidebar}
            <MobileSidebarTrigger />
          </div>
        )}
        <SideNavigationInset sidebarRendered={sidebarRendered}>
          {children}
        </SideNavigationInset>
      </AppChromeProvider>
    </SidebarProvider>
  )
}
